import React, { useState } from 'react';
import Overlay from '../../components/Overlay';
import ActionButton from '../../components/ActionButton';
import InlineMessage from '../../components/InlineMessage';
import useMessage from '../../lib/useMessage';
import { MAKE_HORIZONS, requestOwnShelfMake } from '../../lib/branchTransfers';
import { localDate } from '../../lib/format';
import { controlHeight } from '../../lib/tiers';
import { qtyBox, QTY_BOX_CLASS } from '../../lib/qtyBox';

// MAKE THESE OURSELVES — #580 part (a), gate 553's addendum D21-D27, Session
// 79.21b.
//
// Houston has 6 steel frames and reorders at 20. Ben, an installer, taps the row
// on Stock Home and lands here with 14 filled in: two taps, and nothing reaches
// Purchasing. Kyle, the fabrication manager, opens the same sheet and sees a
// Ready by date instead of "who confirms it" — his request goes straight onto
// To make, confirmed by him (D23).
//
// ONE CALL SAVES IT (B3, locked rule 7): request_own_shelf_make() writes the
// request and its line together, and when the person may confirm, approves it in
// the same transaction. The database decides who may ask — only somebody at the
// branch that makes the item — and refuses anybody else however the button got
// pressed.
//
// A NEW SHEET rather than a mode of the transfer request sheet (F5): one item,
// no branch to choose, a different write. The date choices are the MAKE ones —
// This week first, never Today (callout V) — from the one list in
// branchTransfers.js, and the dates are LOCAL days (#596), never the UTC day.

// The neutral "not finished yet" panel, as RecordProductionSheet uses it:
// ActionButton's own default reason is 11px #7A7A7A, which measures 4.29 against
// the 4.5 floor.
const NEUTRAL = { bg: '#F4F6F8', fg: '#48545F', br: '#DCE2E8', bar: '#8A97A3' };
const BLOCKED_REASON = {
  fontSize: 12, color: NEUTRAL.fg, background: NEUTRAL.bg,
  border: `1px solid ${NEUTRAL.br}`, borderLeft: `3px solid ${NEUTRAL.bar}`,
  borderRadius: 6, padding: '8px 10px', marginTop: 8,
};

const LABEL = { fontSize: 12, fontWeight: 600, color: '#3A3A3A', display: 'block', marginBottom: 6 };

// Locked rule 12 — never below 16px on a text entry.
const INPUT = {
  width: '100%', fontSize: 16, padding: '9px 10px',
  border: '1px solid #CFCFCF', borderRadius: 8, boxSizing: 'border-box',
};

// The darkened inventory green: white on the locked #1D9E75 measures 3.39 and
// fails; #0F6E56 measures 6.20 (550/551 F4, 553 F4).
const MAKE_GREEN = '#0F6E56';

// How much brings the shelf back to its reorder point, less what is already
// asked for — so "Make more" does not quietly ask for the same 14 again. With
// no reorder point the row is on the list only because it is out, and the
// item's own reorder quantity is the best guess there is.
export function makeQtyFor(row, alreadyAsked = 0) {
  const onHand = Number(row?.qty_on_hand) || 0;
  if (row?.reorder_point == null) {
    const q = Number(row?.reorder_qty);
    return Number.isFinite(q) && q > 0 ? String(q) : '';
  }
  const short = Math.max(Number(row.reorder_point) - onHand, 0);
  const left = short - (Number(alreadyAsked) || 0);
  return left > 0 ? String(left) : '';
}

export default function MakeForShelfSheet({
  visible, onClose, onDone, onBuyInstead,
  phoneLayout, coarse,
  row,                  // the Stock Home row: branch_id, qty_on_hand, reorder_point, inventory_items
  branchName,
  branchNames = {},
  makerDepartmentId = null,
  openAsks = [],        // onItsWayTo() entries for this item: made_here / asked_here
  canConfirm = false,   // canConfirmSupply() for a request at this branch
  requesterName = null,
  // Gate 553 B4: only someone AT the making branch may ask. A manager looking
  // at another branch's Stock Home gets the sentence instead of a form the
  // database would refuse.
  unavailableReason = null,
}) {
  const alreadyAsked = openAsks.reduce((s, e) => s + (Number(e.qty) || 0), 0);
  const [qty, setQty] = useState(() => makeQtyFor(row, alreadyAsked));
  const [days, setDays] = useState(MAKE_HORIZONS[0].days);   // This week
  const [readyBy, setReadyBy] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const { message, type, showError, clearMessage } = useMessage();

  if (!visible || !row) return null;

  const item = row.inventory_items || {};
  const onHand = Number(row.qty_on_hand) || 0;
  const rp = row.reorder_point == null ? null : Number(row.reorder_point);
  const short = rp == null ? null : Math.max(rp - onHand, 0);
  const n = Number(qty);
  const valid = Number.isFinite(n) && n > 0;

  const blocked = !valid ? 'Say how many to make.'
    : (canConfirm && !readyBy) ? 'Set a ready-by date first.'
      : null;

  async function submit() {
    clearMessage();
    setBusy(true);
    const r = await requestOwnShelfMake({
      itemId: item.id,
      qty: n,
      neededBy: localDate(days),
      note,
      confirmedDate: canConfirm ? readyBy : null,
      branchId: row.branch_id,
      manufacturingDepartmentId: makerDepartmentId,
      branchNames,
      requesterName,
      itemName: item.name || null,
      uom: item.uom || null,
    });
    setBusy(false);
    // F1 Error: the database's own sentence, inline; the typed quantity, date
    // and note survive because nothing here resets them.
    if (r.error) { showError(r.error.message || 'That did not save. Nothing was asked for — try again.'); return; }
    onDone?.(r);
  }

  const commitH = controlHeight(coarse, 'commit');
  const minH = controlHeight(coarse, 'min');

  return (
    <Overlay variant={phoneLayout ? 'sheet' : 'modal'} onClose={busy ? undefined : onClose} maxWidth={460}>
      <div style={{ padding: '1.1rem 1.15rem 1.4rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontFamily: 'var(--font-head)', textTransform: 'uppercase',
              letterSpacing: '0.04em', fontSize: 16, fontWeight: 700, color: '#0F0F0F',
            }}>
              {unavailableReason ? `${branchName} makes these` : 'Make these ourselves'}
            </div>
            <div style={{ fontSize: 14, color: '#0F0F0F', marginTop: 4, fontWeight: 600 }}>{item.name}</div>
            {/* U — the arithmetic, so the number below explains itself. */}
            <div style={{ fontSize: 13, color: '#3A3A3A', marginTop: 2 }}>
              {onHand} on hand at {branchName}
              {rp != null && ` · reorder at ${rp} · short by ${short}`}
            </div>
          </div>
          <button onClick={onClose} disabled={busy} aria-label="Close"
            style={{
              flexShrink: 0, width: 44, height: 44, marginTop: -8, marginRight: -8,
              background: 'none', border: 'none', fontSize: 22, lineHeight: 1,
              cursor: 'pointer', color: '#5C5C5C',
            }}>×</button>
        </div>

        {!unavailableReason && (
          <div style={{ fontSize: 13, color: '#3A3A3A', marginTop: 10, lineHeight: 1.45 }}>
            We make this here. It goes on our own shelf when it is done — nothing is bought and nothing ships.
          </div>
        )}

        <InlineMessage type={type} message={message} onDismiss={clearMessage} />

        {unavailableReason ? (
          <>
            <div role="status" style={{
              marginTop: 14, background: NEUTRAL.bg, border: `1px solid ${NEUTRAL.br}`,
              borderLeft: `3px solid ${NEUTRAL.bar}`, borderRadius: 6, padding: '10px 12px',
              fontSize: 13, color: NEUTRAL.fg, lineHeight: 1.45,
            }}>
              {unavailableReason}
            </div>
            <button onClick={onClose} className="btn btn-ghost"
              style={{ marginTop: 14, justifyContent: 'center', minHeight: minH, width: phoneLayout ? '100%' : 'auto', minWidth: 110 }}>
              Close
            </button>
          </>
        ) : (
        <>
        {/* F1 Already asked — said before the quantity, so a second ask is a
            decision rather than an accident (X2). */}
        {openAsks.length > 0 && (
          <div style={{
            marginTop: 12, background: '#EAF5F0', border: '1px solid #CFE6DA',
            borderRadius: 8, padding: '8px 10px', fontSize: 13, color: '#14503B', lineHeight: 1.45,
          }}>
            {openAsks.map((e, i) => (
              <div key={i}>
                {e.transfer?.transfer_number
                  ? `${e.qty} already asked for on ${e.transfer.transfer_number}.`
                  : `${e.qty} already asked for — waiting for a date.`}
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <label style={LABEL} htmlFor="make-own-qty">How many should we make?</label>
          <input
            id="make-own-qty" type="number" inputMode="numeric" min="1" step="1"
            value={qty} onChange={e => setQty(e.target.value)}
            // #612 -- the box you must fill measured 39 against the 40 floor.
            // #708 -- it starts at the proposed number, so it is a count to
            // confirm: the shared confirm-box style, 48px.
            className={QTY_BOX_CLASS}
            style={qtyBox({ width: 140, align: 'left' })}
          />
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={LABEL} id="make-own-when">When do you need them?</div>
          <div role="radiogroup" aria-labelledby="make-own-when" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {MAKE_HORIZONS.map(h => {
              const on = days === h.days;
              return (
                <button key={h.label} role="radio" aria-checked={on} onClick={() => setDays(h.days)}
                  style={{
                    padding: '0 14px', minHeight: minH, borderRadius: 999, fontSize: 13, cursor: 'pointer',
                    border: on ? `1px solid ${MAKE_GREEN}` : '1px solid #D6D6D6',
                    background: on ? MAKE_GREEN : '#FFF', color: on ? '#FFF' : '#3A3A3A',
                    fontWeight: on ? 700 : 500,
                  }}>{h.label}</button>
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={LABEL} htmlFor="make-own-note">
            Anything the shop should know? <span style={{ fontWeight: 400, color: '#5C5C5C' }}>(Optional)</span>
          </label>
          <textarea id="make-own-note" rows={2} value={note} onChange={e => setNote(e.target.value)} style={INPUT} />
        </div>

        {/* W — who confirms it, so the asker knows it is not yet on anybody's
            list. A person who may confirm sets the date instead (D23). */}
        {canConfirm ? (
          <div style={{ marginTop: 14 }}>
            <label style={LABEL} htmlFor="make-own-ready">Ready by</label>
            <input id="make-own-ready" type="date" min={localDate(0)}
              value={readyBy} onChange={e => setReadyBy(e.target.value)}
              style={{ ...INPUT, width: 190, minHeight: minH }} />
          </div>
        ) : (
          <div style={{ marginTop: 14, fontSize: 13, color: '#3A3A3A', lineHeight: 1.45 }}>
            {branchName}'s branch manager or fabrication manager confirms it and sets a date.
          </div>
        )}

        <div style={{
          display: 'flex', flexDirection: phoneLayout ? 'column' : 'row-reverse',
          justifyContent: phoneLayout ? undefined : 'flex-start',
          alignItems: phoneLayout ? 'stretch' : 'center', gap: 10, marginTop: 18,
        }}>
          {/* THE BLOCKED REASON SITS UNDER ITS OWN BUTTON. ActionButton renders
              the button and its reason as siblings, so in a row the reason
              became a third item BETWEEN the two buttons -- seen on a desk. */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: phoneLayout ? 'stretch' : 'flex-end' }}>
          <ActionButton
            onClick={submit} blockedReason={blocked} reasonStyle={BLOCKED_REASON}
            busy={busy} busyLabel={canConfirm ? 'Adding…' : 'Asking…'}
            className="btn btn-primary"
            style={{
              background: MAKE_GREEN, color: '#FFF', justifyContent: 'center', minHeight: commitH,
              ...(phoneLayout ? { width: '100%' } : { minWidth: 170 }),
            }}
          >
            {canConfirm
              ? (valid ? `Add ${n} to To make` : 'Add these to To make')
              : (valid ? `Ask for ${n} to be made` : 'Ask for these to be made')}
          </ActionButton>
          </div>
          <button onClick={onClose} disabled={busy} className="btn btn-ghost"
            style={{ justifyContent: 'center', minHeight: minH, ...(phoneLayout ? { width: '100%' } : { minWidth: 110 }) }}>
            Cancel
          </button>
        </div>
        </>
        )}

        {/* D14's exception, as the sheet's last line and never focused first:
            the week the line is down, the maker is exactly the branch that
            needs a way to buy what it normally makes (550/551 callout 14). */}
        <div style={{ borderTop: '1px solid #EFEFEF', marginTop: 16, paddingTop: 10, fontSize: 13, color: '#3A3A3A' }}>
          {unavailableReason ? `${branchName} makes these.` : 'We make these.'}{' '}
          <button type="button" onClick={onBuyInstead} disabled={busy}
            style={{
              background: 'none', border: 'none', padding: '0 2px', minHeight: minH,
              font: 'inherit', color: '#3A3A3A', textDecoration: 'underline',
              textUnderlineOffset: 3, cursor: 'pointer',
            }}>
            Buy from a vendor instead?
          </button>
        </div>
      </div>
    </Overlay>
  );
}
